import { useCallback, useMemo, useState } from "react";

interface UseClientSelection {
  readonly selectedIds: ReadonlySet<string>;
  readonly isSelected: (id: string) => boolean;
  readonly areAllSelected: (ids: string[]) => boolean;
  readonly isAnySelected: (ids: string[]) => boolean;
  readonly toggle: (id: string) => void;
  readonly toggleAll: (ids: string[]) => void;
  readonly clear: () => void;
}

/** Selection backed by a Set, so lookups stay constant-time when thousands of clients are selected; `toggle` and
 * `clear` keep a stable identity, so memoized rows don't re-render when another row is toggled. */
export function useClientSelection(): UseClientSelection {
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(() => new Set());

  const isSelected = useCallback((id: string) => selectedIds.has(id), [selectedIds]);
  const areAllSelected = useCallback(
    (ids: string[]) => ids.length > 0 && ids.every((id) => selectedIds.has(id)),
    [selectedIds],
  );
  const isAnySelected = useCallback((ids: string[]) => ids.some((id) => selectedIds.has(id)), [selectedIds]);

  const toggle = useCallback((id: string) => {
    setSelectedIds((previous) => {
      const next = new Set(previous);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const toggleAll = useCallback(
    (ids: string[]) => {
      const allSelected = areAllSelected(ids);
      const toggledIds = new Set(ids);
      setSelectedIds((previous) =>
        allSelected ? new Set([...previous].filter((id) => !toggledIds.has(id))) : new Set([...previous, ...ids]),
      );
    },
    [areAllSelected],
  );

  const clear = useCallback(() => setSelectedIds(new Set()), []);

  return useMemo(
    () => ({ selectedIds, isSelected, areAllSelected, isAnySelected, toggle, toggleAll, clear }),
    [selectedIds, isSelected, areAllSelected, isAnySelected, toggle, toggleAll, clear],
  );
}
